<template lang="pug">
.jurisdiction-item
  .jurisdiction-node.flex.items-center.p-2.rounded.cursor-pointer.hover_bg-gray-100.dark_hover_bg-gray-600(
    @click="$emit('toggle', node)"
    :class="{ 'bg-blue-100 dark_bg-blue-900': isSelected }"
  )
    //- Expand/collapse icon for nodes with children
    .expand-icon.mr-2.w-6.h-6.flex.items-center.justify-center.cursor-pointer.hover_bg-gray-200.dark_hover_bg-gray-600.rounded(
      v-if="hasChildren"
      @click.stop="$emit('toggle-expand', node.id)"
    )
      Icon.icon.text-gray-600.dark_text-gray-300(
        :icon="isExpanded ? 'teenyicons:down-small-outline' : 'teenyicons:right-small-outline'"
        class="w-4 h-4"
      )
    //- Placeholder for consistent spacing when no children
    .w-6.mr-2(v-else)
    
    //- Checkbox for selection
    input.checkbox.w-4.h-4.text-primary-600.bg-gray-600.border-gray-500.rounded.focus_ring-primary-500.mr-2(
      type="checkbox"
      :checked="isSelected"
      :indeterminate="isIndeterminate"
      @click.stop
      @change="$emit('toggle', node)"
    )
    
    //- Jurisdiction name
    span.text-gray-900.dark_text-gray-100 {{ node.name }}
  
  //- Render children if expanded
  .children.ml-4(
    v-if="isExpanded && hasChildren"
  )
    JurisdictionTreeNode(
      v-for="childNode in children"
      :key="childNode.id"
      :node="childNode"
      :jurisdictions="jurisdictions"
      :selectedItems="selectedItems"
      :expandedNodes="expandedNodes"
      :depth="depth + 1"
      @toggle="$emit('toggle', $event)"
      @toggle-expand="$emit('toggle-expand', $event)"
    )
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Icon } from '@iconify/vue';
// Component can reference itself recursively in Vue 3

interface JurisdictionNode {
  id: string;
  name: string;
  parentId: string | null;
}

interface Props {
  node: JurisdictionNode;
  jurisdictions: Record<string, JurisdictionNode>;
  selectedItems: string[];
  expandedNodes: Set<string>;
  depth?: number;
}

const props = withDefaults(defineProps<Props>(), {
  depth: 0
});

const emit = defineEmits<{
  toggle: [node: JurisdictionNode];
  'toggle-expand': [nodeId: string];
}>();

// Helper function to check if a node has children
const hasChildren = computed(() => {
  return Object.values(props.jurisdictions).some(
    node => node.parentId === props.node.id
  );
});

// Helper function to get children of a node
const children = computed(() => {
  return Object.values(props.jurisdictions).filter(
    node => node.parentId === props.node.id
  );
});

// Check if this node is expanded
const isExpanded = computed(() => {
  return props.expandedNodes.has(props.node.id);
});

// Check if this node is selected
const isSelected = computed(() => {
  return props.selectedItems.includes(props.node.name);
});

// Check if this node should show indeterminate state
// Level 1 (depth 0) should NOT show indeterminate, Level 2+ should
const isIndeterminate = computed(() => {
  // Don't show indeterminate for Level 1 (depth 0)
  if (props.depth === 0) {
    return false;
  }

  // Check if this node has children
  if (!hasChildren.value) {
    return false;
  }

  const childNames = children.value.map(child => child.name);
  const selectedChildren = childNames.filter(childName =>
    props.selectedItems.includes(childName)
  );

  // Partial selection: some children selected but not all, and parent is not selected
  return (
    selectedChildren.length > 0 &&
    selectedChildren.length < childNames.length &&
    !props.selectedItems.includes(props.node.name)
  );
});
</script>

<style scoped>
.checkbox {
  accent-color: #3b82f6;
}

.jurisdiction-node {
  transition: background-color 0.15s ease-in-out;
}

.expand-icon {
  transition: transform 0.15s ease-in-out;
}

.expand-icon:hover {
  transform: scale(1.1);
}
</style>
